import { ApiError } from '@/api/rest';
import { describeApiError } from '@/ui/notice';
import { plural } from '@/ui/plural';
import type { ConnectionStatus } from '@/transport/types';

/** Код комнаты: 4 символа A-Z0-9, ровно как `RoomCode` в openapi.yaml. */
export const ROOM_CODE_RE = /^[A-Z0-9]{4}$/;

const STATUS_LABEL: Record<ConnectionStatus, string> = {
  idle: 'нет соединения',
  connecting: 'подключаемся…',
  online: 'на связи',
  reconnecting: 'переподключаемся…',
  closed: 'комната закрыта',
  fatal: 'соединение не восстановить',
};

export function statusLabel(status: ConnectionStatus): string {
  return STATUS_LABEL[status];
}

/** «3 ИГРОКА», «5 ИГРОКОВ», «1 ИГРОК» — под MainButton, поэтому капсом. */
export function playersWord(n: number): string {
  return plural(n, ['ИГРОК', 'ИГРОКА', 'ИГРОКОВ']);
}

/** «193 КАРТОЧКИ», «12 КАРТОЧЕК», «1 КАРТОЧКА» — витрина пака. */
export function cardsWord(n: number): string {
  return plural(n, ['КАРТОЧКА', 'КАРТОЧКИ', 'КАРТОЧЕК']);
}

/**
 * То же для витрины паков: 404 здесь означает снятую с витрины версию, а не
 * ненайденную комнату, и общий текст сказал бы человеку неправду.
 */
export function describeDeckError(err: unknown): string {
  if (err instanceof ApiError && err.status === 404) return 'Пак снят с витрины';
  return describeError(err);
}

/**
 * Отказ сервера человеческим языком. Общая часть — в `describeApiError`;
 * здесь только то, что знает лобби: 404 в его запросах всегда про комнату.
 *
 * 401 и 426 доходят сюда вместе с тупиком: RestClient уже позвал `onBlocked`,
 * и через кадр экран сменится на BlockedScreen. Совета «попробуйте ещё раз»
 * поэтому нет ни там, ни там — повтор не работает.
 */
export function describeError(err: unknown): string {
  if (err instanceof ApiError && err.status === 404) return 'Комната не найдена';
  // 501 у комнат означает не «ручку не написали» (её написали, шаг В10), а
  // сервер, собранный не до конца: чаще всего в базе нет ни одной
  // опубликованной колоды, и версию партии взять неоткуда. Общий текст
  // «пока не поддерживается» отправил бы человека ждать вместо `deck-release`.
  if (err instanceof ApiError && err.isNotImplemented) {
    return 'Сервер не может создать комнату: на нём нет опубликованной колоды';
  }
  return describeApiError(err);
}

/**
 * То же для попытки войти в комнату. REST-проверка кода проходит и у комнаты,
 * в которую уже не пускают, — отказ приезжает отказом хендшейка (`not_allowed`
 * опоздавшему, `unauthorized` не-хосту hotseat), и причину знает только он.
 * Транспорт кладёт её в `fatal`, поэтому она берётся оттуда, а не из
 * реджекта: у самого промиса `connect` нет ни кода, ни разбора.
 */
export function describeJoinError(err: unknown, fatal: string | null): string {
  if (err instanceof ApiError) return describeError(err);
  return fatal ?? describeError(err);
}
